package top.wcpe.allincore.dsl.ir.model.action

import top.wcpe.allincore.dsl.ir.expr.ExprBlobReader
import top.wcpe.allincore.dsl.ir.expr.ExprNode
import top.wcpe.allincore.dsl.ir.expr.StmtNode
import top.wcpe.allincore.dsl.ir.model.internal.ReadOnlyList

/**
 * screen 级函数（C4）：保存形参列表与 body 字节流。
 *
 * 设计与 [UiAction] 对齐：
 * - 不直接持有反序列化后的 [StmtNode] 列表，仅保存 raw bytes，便于 wire / hash / 缓存。
 * - body 字节序列与 `action body` 格式完全一致（`u16 stmtCount + stmts`），
 *   因此可复用 [ExprBlobReader.readActionBody]。
 *
 * M5-b：新增 `defaultBlobs` 字段，按形参顺序保存默认值表达式 blob；
 * `null` 表示该形参无默认值。空列表表示全部无默认值（旧行为）。
 */
class IrFun(
    name: String?,
    params: List<String>?,
    bodyBytes: ByteArray?,
    defaultBlobs: List<ByteArray?>?,
) {
    val name: String = name ?: throw NullPointerException("name")
    val params: List<String> = ReadOnlyList(ArrayList(params ?: throw NullPointerException("params")))

    // 防御式拷贝 byte[]，避免外部篡改内部字节。
    private val bodyBytesInternal: ByteArray =
        (bodyBytes ?: throw NullPointerException("bodyBytes")).copyOf()

    /** 与 [params] 等长；元素为默认值 expr blob，`null` 表示无默认值。空列表表示全部无默认值。 */
    private val defaultBlobsInternal: List<ByteArray?>

    init {
        val db = defaultBlobs ?: throw NullPointerException("defaultBlobs")
        require(db.isEmpty() || db.size == this.params.size) {
            "defaultBlobs 长度与 params 不一致: " + db.size + " vs " + this.params.size
        }
        // 深拷贝 byte[] 防止外部篡改
        val copy = ArrayList<ByteArray?>(db.size)
        for (b in db) {
            copy.add(b?.copyOf())
        }
        defaultBlobsInternal = ReadOnlyList(copy)
    }

    /** 旧构造器，无默认值；保留用于兼容现有调用点。 */
    constructor(name: String?, params: List<String>?, bodyBytes: ByteArray?) :
        this(name, params, bodyBytes, emptyList())

    /**
     * 返回 body 字节流的副本。
     * 复刻原 Java `getBodyBytes()`：暴露为属性 `bodyBytes`，保留 private backing + 防御拷贝。
     */
    val bodyBytes: ByteArray
        get() = bodyBytesInternal.copyOf()

    /**
     * 返回默认值 blob 列表（外层不可变；元素 byte[] 为内部副本，调用方不应修改）。
     * 复刻原 Java `getDefaultBlobs()`：暴露为属性 `defaultBlobs`。
     */
    val defaultBlobs: List<ByteArray?>
        get() = defaultBlobsInternal

    /** 反序列化 body 为 [StmtNode] 列表（每次调用都会重新解码，调用方可缓存）。 */
    fun decodeBody(): List<StmtNode> = ExprBlobReader.readActionBody(bodyBytesInternal)

    /**
     * 反序列化第 [index] 个形参的默认值表达式；
     * 若该形参无默认值或本函数无任何默认值，返回 `null`。
     */
    fun decodeDefault(index: Int): ExprNode? {
        if (defaultBlobsInternal.isEmpty()) return null
        val blob = defaultBlobsInternal[index] ?: return null
        return ExprBlobReader.readExpr(blob)
    }

    override fun equals(other: Any?): Boolean {
        if (this === other) return true
        if (other !is IrFun) return false
        if (name != other.name || params != other.params ||
            !bodyBytesInternal.contentEquals(other.bodyBytesInternal)
        ) {
            return false
        }
        if (defaultBlobsInternal.size != other.defaultBlobsInternal.size) return false
        for (i in defaultBlobsInternal.indices) {
            if (!defaultBlobsInternal[i].contentEquals(other.defaultBlobsInternal[i])) {
                return false
            }
        }
        return true
    }

    override fun hashCode(): Int {
        // 复刻 Objects.hash(name, params, Arrays.hashCode(bodyBytes)) 再逐元素 31 倍累乘。
        var h = 1
        h = 31 * h + name.hashCode()
        h = 31 * h + params.hashCode()
        h = 31 * h + bodyBytesInternal.contentHashCode()
        for (b in defaultBlobsInternal) {
            h = 31 * h + b.contentHashCode()
        }
        return h
    }

    override fun toString(): String =
        "IrFun(" + name + params + ",bodyBytes=" + bodyBytesInternal.size +
            ",defaults=" + defaultBlobsInternal.size + ")"
}
